import { getItem, setItem, removeItem } from "@lib/storage";
import {
  getFpRequestId,
  getGateDeviceInfo,
  saveFpReuseTtl,
} from "@lib/fingerprint";
import { api, uploadPrivateOnboardingImage } from "@services/api";

// ─── Device/identity fraud blocks ───
// The backend returns 403 with these codes when the device is on the fraud
// blocklist (DEVICE_BLOCKED) or the phone/aadhaar belongs to a fraud-flagged
// DB (ONBOARDING_BLOCKED). Neither is retryable — surface a terminal message.
export const BLOCKED_MESSAGE_HI =
  "सॉरी, यह डिवाइस या अकाउंट ब्लॉक कर दिया गया है। सहायता के लिए अपने स्पोक एग्जीक्यूटिव से संपर्क करें।";

export const getBlockedErrorMessage = (err: unknown): string | null => {
  const e = err as { response?: { data?: { error?: string } } };
  const code = e?.response?.data?.error;
  return code === "DEVICE_BLOCKED" || code === "ONBOARDING_BLOCKED"
    ? BLOCKED_MESSAGE_HI
    : null;
};
import type {
  CheckPhoneResult,
  CheckAadhaarResult,
  PersonalDetailsForm,
  DeliveryHub,
  RiderType,
} from "@screens/Onboarding/types";
import type { OnboardingData } from "@screens/Onboarding/OnboardingContext";
import type {
  OnboardingFlowConfig,
  DocumentItemConfig,
} from "@screens/Onboarding/flowConfig.types";

// ─── Registration session token ───
// verify-otp returns a 14-day session UUID. Stored separately from the
// leader `user_token`; sent as Authorization on protected onboarding calls.
const REGISTRATION_TOKEN_KEY = "db_registration_token";

const getRegistrationToken = async (): Promise<string | null> => {
  return getItem(REGISTRATION_TOKEN_KEY);
};

const setRegistrationToken = async (token: string): Promise<void> => {
  await setItem(REGISTRATION_TOKEN_KEY, token);
};

export const clearRegistrationToken = async (): Promise<void> => {
  await removeItem(REGISTRATION_TOKEN_KEY);
};

const withRegistrationAuth = async () => {
  const token = await getRegistrationToken();
  return { headers: { Authorization: token ?? "" } };
};

// ─── Backend response shapes ───
// Raw response enums from /api/cpdb/db-onboarding/*. Mapped to app types below.
type BackendCheckPhoneStatus =
  | "ACTIVE_DB"
  | "ACTIVE_CP"
  | "INACTIVE_DB_VERIFIED"
  | "INACTIVE_DB_UNVERIFIED"
  | "INACTIVE_CP_VERIFIED"
  | "INACTIVE_CP_UNVERIFIED"
  | "NEW";

interface BackendCheckPhoneResponse {
  status: BackendCheckPhoneStatus;
  phone_number: string;
  existing_phone?: string;
}

type BackendCheckAadhaarStatus =
  | "VERIFIED_ACTIVE_DB"
  | "VERIFIED_ACTIVE_CP"
  | "VERIFIED_INACTIVE"
  | "NOT_FOUND";

interface BackendCheckAadhaarResponse {
  status: BackendCheckAadhaarStatus;
  registered_phone?: string;
  registered_phone_unmasked?: string;
  full_name?: string;
  leader_id?: number;
  leader_state?: string;
}

type ConfirmAadhaarConversionAction = "CP_TO_DB" | "REVIVE_INACTIVE";

interface BackendConfirmAadhaarConversionResponse {
  status: "CONVERTED";
  action: ConfirmAadhaarConversionAction;
  target_phone: string;
  target_phone_masked: string;
  kyc_onboard_id: number;
  next_step: "LOGIN_WITH_REGISTERED_PHONE" | "SpokeSelection";
}

export interface ConfirmAadhaarConversionResult {
  action: ConfirmAadhaarConversionAction;
  targetPhone: string;
  targetPhoneMasked: string;
  nextStep: "LOGIN_WITH_REGISTERED_PHONE" | "SpokeSelection";
}

interface BackendVerifyOtpResponse {
  token: string;
  phone_number: string;
  expired_at: string;
  flow_config?: OnboardingFlowConfig;
}

export interface VerifyOtpResult {
  flowConfig?: OnboardingFlowConfig;
}

// ─── Check Phone ───
export const checkPhone = async (
  phoneNumber: string,
): Promise<CheckPhoneResult> => {
  const { data } = await api.post<BackendCheckPhoneResponse>(
    "/cpdb/db-onboarding/check-phone",
    { phone_number: phoneNumber },
  );

  switch (data.status) {
    case "ACTIVE_DB":
      return {
        case: "active_db",
        registeredPhone: data.existing_phone ?? phoneNumber,
      };
    case "ACTIVE_CP":
      // Backend doesn't return cp_id today; fall back to phone as identifier.
      return { case: "active_cp", cpId: data.phone_number };
    case "INACTIVE_DB_VERIFIED":
      return { case: "inactive_db_kyc_verified", registeredPhone: phoneNumber };
    case "INACTIVE_DB_UNVERIFIED":
      return { case: "inactive_db_kyc_not_verified" };
    case "INACTIVE_CP_VERIFIED":
      return { case: "inactive_cp_kyc_verified", registeredPhone: phoneNumber };
    case "INACTIVE_CP_UNVERIFIED":
      return { case: "inactive_cp_kyc_not_verified" };
    case "NEW":
    default:
      return { case: "new_user" };
  }
};

// ─── OTP ───
export const sendOtp = async (
  phoneNumber: string,
  hash?: string,
): Promise<void> => {
  const response = await api.post<{ sent?: boolean; fp_reuse_ttl_ms?: number }>(
    "/cpdb/db-onboarding/send-otp",
    {
      phone_number: phoneNumber,
      hash,
    },
  );
  // Reuse window for the cached Fingerprint requestId; also covers the later
  // submit-kyc, which has no send-OTP of its own. Absent → 5-min fallback.
  saveFpReuseTtl(response?.data?.fp_reuse_ttl_ms);
};

export const verifyOtp = async (
  phoneNumber: string,
  otp: string,
  options?: { convertFromCp?: boolean },
): Promise<VerifyOtpResult> => {
  // Device gate: fresh fp_request_id per attempt (backend rejects stale
  // events); device_info is the untrusted fallback trail when FP is down.
  const [fpRequestId, deviceInfo] = await Promise.all([
    getFpRequestId(phoneNumber, "onboarding-verify-otp"),
    getGateDeviceInfo(),
  ]);
  const { data } = await api.post<BackendVerifyOtpResponse>(
    "/cpdb/db-onboarding/verify-otp",
    {
      phone_number: phoneNumber,
      otp,
      convert_from_cp: options?.convertFromCp === true ? true : undefined,
      fp_request_id: fpRequestId ?? undefined,
      device_info: deviceInfo,
    },
  );
  // Persist the session token for subsequent protected calls.
  if (data?.token) await setRegistrationToken(data.token);
  return { flowConfig: data?.flow_config };
};

// ─── Check Aadhaar (protected) ───
export const checkAadhaar = async (
  aadhaarNumber: string,
): Promise<CheckAadhaarResult> => {
  const { data } = await api.post<BackendCheckAadhaarResponse>(
    "/cpdb/db-onboarding/check-aadhaar",
    { aadhaar_number: aadhaarNumber },
    await withRegistrationAuth(),
  );

  switch (data.status) {
    case "VERIFIED_ACTIVE_DB":
      return {
        case: "active_db",
        registeredPhone: data.registered_phone ?? "",
        fullName: data.full_name,
      };
    case "VERIFIED_ACTIVE_CP":
      return {
        case: "active_cp",
        registeredPhone: data.registered_phone ?? "",
        fullName: data.full_name,
      };
    case "VERIFIED_INACTIVE":
      return {
        case: "inactive",
        registeredPhone: data.registered_phone ?? "",
        fullName: data.full_name,
      };
    case "NOT_FOUND":
    default:
      return { case: "not_found" };
  }
};

// ─── Confirm Aadhaar Conversion (protected) ───
// Backs the two follow-up actions surfaced by /check-aadhaar:
//   CP_TO_DB        — Case 1.2 "Haan" on the CP→DB conversion popup.
//                     Backend suspends the leader and copies KYC onto the
//                     LEADER's registered phone. FE must then ask the user
//                     to log in with that phone (next_step =
//                     LOGIN_WITH_REGISTERED_PHONE).
//   REVIVE_INACTIVE — Case 1.3 auto-revive when an inactive registration
//                     matches the entered aadhaar. KYC is copied onto the
//                     CURRENT session phone; FE continues in-session to
//                     spoke selection (next_step = SpokeSelection).
export const confirmAadhaarConversion = async (
  aadhaarNumber: string,
  action: ConfirmAadhaarConversionAction,
): Promise<ConfirmAadhaarConversionResult> => {
  const { data } = await api.post<BackendConfirmAadhaarConversionResponse>(
    "/cpdb/db-onboarding/confirm-aadhaar-conversion",
    { aadhaar_number: aadhaarNumber, action },
    await withRegistrationAuth(),
  );
  return {
    action: data.action,
    targetPhone: data.target_phone,
    targetPhoneMasked: data.target_phone_masked,
    nextStep: data.next_step,
  };
};

// ─── Image / video upload ───
// Uses the existing /cpdb/upload-private-onboarding-image endpoint. The
// cp_db_kyc_onboard stub row (required by the backend middleware) is created
// server-side at verify-otp time, so uploads work immediately after OTP success.
export interface OnboardingFileInput {
  uri: string;
  name: string;
  type: string; // e.g. "image/jpeg", "video/mp4"
}

export const uploadOnboardingImage = async (
  file: OnboardingFileInput,
  phoneNumber: string,
): Promise<string> => {
  const form = new FormData();
  // React Native multipart shape — the RN Blob/File substitute is the
  // { uri, name, type } object.
  form.append("image", file as unknown as Blob);
  form.append("mobile", phoneNumber);
  const { data } = await uploadPrivateOnboardingImage(form);
  if (!data?.url) {
    throw new Error("UPLOAD_FAILED");
  }
  return data.url as string;
};

// ─── KYC submit (batch) ───
// Single call to /cpdb/db-onboarding/submit-kyc with all fields + uploaded URLs.
// Fire this once after the user has completed PersonalDetails + Documents +
// DocumentOwnership (+ YourDocuments if family) (+ AddressDetails if laggan).
export interface SubmitKycInput {
  riderType: RiderType;
  isFamilyKyc: boolean;
  // Own phone — linkedId for the device-fraud fingerprint at submit time
  phoneNumber: string;

  // Personal details
  name: string;
  dob: string;
  city: string;
  homePhoneNumber?: string;
  refereeName?: string;
  refereeNumber?: string;

  // Self KYC (all required)
  aadhaarNumber: string;
  aadhaarFrontUrl: string;
  aadhaarBackUrl: string;
  panNumber: string;
  panFrontUrl: string;
  panBackUrl: string;
  drivingLicenseNumber: string;
  drivingLicenseFrontUrl: string;
  drivingLicenseBackUrl: string;
  selfieUrl: string;

  // Family-KYC personal doc — required only when isFamilyKyc
  personalDocType?: "AADHAAR" | "PAN" | "DL";
  personalDocNumber?: string;
  personalDocFrontUrl?: string;
  personalDocBackUrl?: string;

  // Laggan-only — required only when riderType === 'laggan'
  lagganVideoUrl?: string;
  lagganFcName?: string;
  addressPlot?: string;
  addressArea?: string;
  addressPincode?: string;
  addressState?: string;
  addressCity?: string;
}

interface SubmitKycResponse {
  status: "PROCESSING";
  kyc_onboard_id: number;
}

export const submitKyc = async (
  input: SubmitKycInput,
): Promise<SubmitKycResponse> => {
  const [fpRequestId, deviceInfo] = await Promise.all([
    getFpRequestId(input.phoneNumber, "onboarding-submit-kyc"),
    getGateDeviceInfo(),
  ]);
  const payload = {
    fp_request_id: fpRequestId ?? undefined,
    device_info: deviceInfo,
    db_type: input.riderType === "laggan" ? "LAGGAN" : "NORMAL",
    is_family_kyc: input.isFamilyKyc,
    name: input.name,
    dob: input.dob,
    city: input.city,
    home_phone_number: input.homePhoneNumber || undefined,
    referee_name: input.refereeName || undefined,
    referee_number: input.refereeNumber || undefined,

    aadhaar_number: input.aadhaarNumber,
    aadhaar_front_url: input.aadhaarFrontUrl,
    aadhaar_back_url: input.aadhaarBackUrl,
    pan_number: input.panNumber,
    pan_front_url: input.panFrontUrl,
    pan_back_url: input.panBackUrl,
    driving_license_number: input.drivingLicenseNumber,
    driving_license_front_url: input.drivingLicenseFrontUrl,
    driving_license_back_url: input.drivingLicenseBackUrl,
    selfie_url: input.selfieUrl,

    personal_doc_type: input.isFamilyKyc ? input.personalDocType : undefined,
    personal_doc_number: input.isFamilyKyc
      ? input.personalDocNumber
      : undefined,
    personal_doc_front_url: input.isFamilyKyc
      ? input.personalDocFrontUrl
      : undefined,
    personal_doc_back_url: input.isFamilyKyc
      ? input.personalDocBackUrl
      : undefined,

    laggan_video_url:
      input.riderType === "laggan" ? input.lagganVideoUrl : undefined,
    laggan_fc_name:
      input.riderType === "laggan" ? input.lagganFcName : undefined,
    address_plot: input.riderType === "laggan" ? input.addressPlot : undefined,
    address_area: input.riderType === "laggan" ? input.addressArea : undefined,
    address_pincode:
      input.riderType === "laggan" ? input.addressPincode : undefined,
    address_state:
      input.riderType === "laggan" ? input.addressState : undefined,
    address_city: input.riderType === "laggan" ? input.addressCity : undefined,
  };

  const { data } = await api.post<SubmitKycResponse>(
    "/cpdb/db-onboarding/submit-kyc",
    payload,
    await withRegistrationAuth(),
  );
  return data;
};

/**
 * Reads OnboardingContext state, builds the SubmitKycInput, fires submitKyc.
 * documentConfigs comes from the backend flow_config (verify-otp response) and
 * controls which document photos are required before submitting.
 */
export const submitKycFromContext = async (
  d: OnboardingData,
  documentConfigs: DocumentItemConfig[],
): Promise<SubmitKycResponse> => {
  const isFamilyKyc = d.documentOwnership === "family";
  const isLaggan = d.riderType === "laggan";

  const required = (key: string, value: unknown) => {
    if (!value) throw new Error(`MISSING:${key}`);
  };
  const isRequired = (id: DocumentItemConfig["id"]) =>
    documentConfigs.find((c) => c.id === id)?.required ?? false;

  required("fullName", d.fullName);
  required("dateOfBirth", d.dateOfBirth);
  required("city", d.city);
  required("aadhaarNumber", d.aadhaarNumber);
  if (isRequired("aadhaarCard")) {
    required("aadhaarFront", d.aadhaarPhotos.front);
    required("aadhaarBack", d.aadhaarPhotos.back);
  }
  if (isRequired("panCard")) {
    required("panNumber", d.panNumber);
    required("panFront", d.panPhotos.front);
    required("panBack", d.panPhotos.back);
  }
  if (isRequired("drivingLicense")) {
    required("drivingLicenseNumber", d.drivingLicenseNumber);
    required("drivingLicenseFront", d.drivingLicensePhotos.front);
    required("drivingLicenseBack", d.drivingLicensePhotos.back);
  }
  required("selfieUrl", d.selfieUrl);

  if (isFamilyKyc) {
    required("personalDocType", d.personalDocType);
    required("personalDocFront", d.personalDocPhotos.front);
  }
  if (isLaggan) {
    required("lagganVideoUrl", d.lagganVideoUrl);
    required("addressPincode", d.addressPincode);
  }

  return submitKyc({
    riderType: d.riderType,
    isFamilyKyc,
    phoneNumber: d.phoneNumber,
    name: d.fullName,
    dob: d.dateOfBirth,
    city: d.city,
    homePhoneNumber: d.homePhoneNumber || undefined,
    refereeName: d.refereeName || undefined,
    refereeNumber: d.refereeNumber || undefined,

    aadhaarNumber: d.aadhaarNumber,
    aadhaarFrontUrl: d.aadhaarPhotos.front ?? "",
    aadhaarBackUrl: d.aadhaarPhotos.back ?? "",
    panNumber: d.panNumber,
    panFrontUrl: d.panPhotos.front ?? "",
    panBackUrl: d.panPhotos.back ?? "",
    drivingLicenseNumber: d.drivingLicenseNumber,
    drivingLicenseFrontUrl: d.drivingLicensePhotos.front ?? "",
    drivingLicenseBackUrl: d.drivingLicensePhotos.back ?? "",
    selfieUrl: d.selfieUrl ?? "",

    personalDocType: isFamilyKyc ? d.personalDocType ?? undefined : undefined,
    personalDocNumber: isFamilyKyc ? d.personalDocNumber : undefined,
    personalDocFrontUrl: isFamilyKyc
      ? d.personalDocPhotos.front ?? undefined
      : undefined,
    personalDocBackUrl: isFamilyKyc
      ? d.personalDocPhotos.back ?? undefined
      : undefined,

    lagganVideoUrl: isLaggan ? d.lagganVideoUrl ?? undefined : undefined,
    lagganFcName: isLaggan ? d.lagganFcName || undefined : undefined,
    addressPlot: isLaggan ? d.addressPlot || undefined : undefined,
    addressArea: isLaggan ? d.addressArea || undefined : undefined,
    addressPincode: isLaggan ? d.addressPincode || undefined : undefined,
    addressState: isLaggan ? d.addressState || undefined : undefined,
    addressCity: isLaggan ? d.addressCity || undefined : undefined,
  });
};

// ─── Legacy stubs kept so old screen imports don't break; fold into submitKyc ───
export const submitPersonalDetails = async (
  _form: PersonalDetailsForm,
): Promise<void> => {
  throw new Error(
    "submitPersonalDetails is deprecated — call submitKyc(input) at end of flow",
  );
};

export interface DocumentsPayload {
  aadhaarFront: string;
  aadhaarBack: string;
  panFront: string;
  panBack: string;
  dlFront: string;
  dlBack: string;
  photo: string;
  lagganVideoUri?: string;
}

export const submitDocuments = async (
  _payload: DocumentsPayload,
): Promise<void> => {
  throw new Error(
    "submitDocuments is deprecated — call submitKyc(input) at end of flow",
  );
};

// ─── Spokes listing (public) ───
interface BackendSpoke {
  cp_id: string;
  name: string;
  formatted_address?: string;
  lat?: string | number;
  lng?: string | number;
  distance_km?: string | number;
}

type SpokeListParams =
  | { mode: "geo"; lat: number; lng: number; radius_km?: number }
  | { mode: "state_city"; state: string; city: string };

const mapSpoke = (s: BackendSpoke): DeliveryHub => ({
  id: s.cp_id,
  name: s.name,
  address: s.formatted_address ?? "",
  hasIncentive: false,
  distanceKm: s.distance_km != null ? Number(s.distance_km) : undefined,
  lat: s.lat != null ? Number(s.lat) : undefined,
  lng: s.lng != null ? Number(s.lng) : undefined,
});

export const getSpokeList = async (
  params: SpokeListParams,
): Promise<DeliveryHub[]> => {
  const { data } = await api.get<{ spokes: BackendSpoke[] }>(
    "/cpdb/db-onboarding/cmoSpokes",
    { params },
  );
  return (data.spokes ?? []).map(mapSpoke);
};

export interface SpokeFilters {
  states: string[];
  cities_by_state: Record<string, string[]>;
}

export const getSpokeFilters = async (): Promise<SpokeFilters> => {
  const { data } = await api.get<SpokeFilters>(
    "/cpdb/db-onboarding/cmoSpokes/filters",
  );
  return data;
};

// ─── Spoke selection (protected) ───
export interface OnboardingSubmitPayload {
  spokeId: string;
  riderType: RiderType;
}

export const submitOnboarding = async (
  payload: OnboardingSubmitPayload,
): Promise<void> => {
  await api.post(
    "/cpdb/db-onboarding/select-spoke",
    { cp_id: payload.spokeId },
    await withRegistrationAuth(),
  );
};

// ─── Onboarding status (protected) ───
export interface RejectedKycItem {
  name: string;
  comment?: string | null;
}

export interface OnboardingStatus {
  phone_number: string;
  kyc_status:
    | "PENDING"
    | "PROCESSING"
    | "VERIFIED"
    | "REJECTED"
    | "FAILED"
    | null;
  cp_id?: string | null;
  db_type?: "NORMAL" | "LAGGAN" | null;
  is_family_kyc?: boolean;
  has_submitted?: boolean;
  resume_step?:
    | "AadhaarVerification"
    | "PersonalDetails"
    | "SignUpSuccessful"
    | "ApplicationSubmitted"
    | "SpokeSelection";
  spoke_name?: string | null;
  spoke_address?: string | null;
  updated_at?: string;
  rejected_items?: RejectedKycItem[];
  aadhaar_number?: string | null;
  pan_number?: string | null;
}

export const getOnboardingStatus = async (): Promise<OnboardingStatus> => {
  const { data } = await api.get<OnboardingStatus>(
    "/cpdb/db-onboarding/status",
    await withRegistrationAuth(),
  );
  return data;
};

// ─── Resubmit documents after KYC rejection ───
export interface ResubmitDocumentsInput {
  aadhaarFrontUrl?: string;
  aadhaarBackUrl?: string;
  panFrontUrl?: string;
  panBackUrl?: string;
  drivingLicenseFrontUrl?: string;
  drivingLicenseBackUrl?: string;
  selfieUrl?: string;
  personalDocFrontUrl?: string;
  personalDocBackUrl?: string;
  aadhaarNumber?: string;
  panNumber?: string;
}

export const resubmitDocuments = async (
  input: ResubmitDocumentsInput,
): Promise<void> => {
  await api.post(
    "/cpdb/db-onboarding/resubmit-documents",
    {
      aadhaar_front_url: input.aadhaarFrontUrl,
      aadhaar_back_url: input.aadhaarBackUrl,
      pan_front_url: input.panFrontUrl,
      pan_back_url: input.panBackUrl,
      driving_license_front_url: input.drivingLicenseFrontUrl,
      driving_license_back_url: input.drivingLicenseBackUrl,
      selfie_url: input.selfieUrl,
      personal_doc_front_url: input.personalDocFrontUrl,
      personal_doc_back_url: input.personalDocBackUrl,
      aadhaar_number: input.aadhaarNumber,
      pan_number: input.panNumber,
    },
    await withRegistrationAuth(),
  );
};
